import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { useAuth } from '../../Routes/AuthProvider'
import { InvoiceSignaturePreview } from './InvoiceSignaturePreview'

import { useLoadingIds } from '../../../hooks/useLoadingIds'
import {
  FetchInvoiceSignatureResponse,
  invoicesApi,
  UserProfile,
} from '../../../api/swagger'
import useEndpoint from '../../../hooks/useEndpoint'
import { InvoiceSignatureForm } from '../../Invoice/InvoiceSignatureForm'
import { useUser } from '../../../UserContext'
import { Box } from '@mui/material'
import LoadingProgress from '../../Elements/LoadingProgress'
import { useTranslation } from 'react-i18next'
import {
  SnackbarSeverity,
  useSnackbarContext,
} from '../../Context/SnackbarContext'

export const defaultAddress = {
  locationName: '',
  streetAddress1: '',
  streetAddress2: '',
  city: '',
  state: '',
  zip: '',
  countryCode: 'US',
}
export const emptyInvoiceSignatureInformation: FetchInvoiceSignatureResponse = {
  closingMessage: '',
  payableTo: '',
  useDefaultSignature: false,
  address: undefined,
  invoiceSenderKey: 0,
  phoneNumber: '',
}

export const mapUserToInvoiceSignature = (
  user: UserProfile
): FetchInvoiceSignatureResponse => ({
  payableTo: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
  address: user.address,
  email: user.email ?? user.username,
  useDefaultSignature: false,
  closingMessage: 'Thank you!',
  phoneNumber: user.phone ?? '',
  invoiceSenderKey: +user.id,
})

const addressField = [
  'locationName',
  'streetAddress1',
  'streetAddress2',
  'city',
  'state',
  'zip',
  'countryCode',
]

export const InvoiceProfileLayout: React.FC = () => {
  const { setSnackbarState, setSnackbarMessage, setSnackbarSeverity } =
    useSnackbarContext()
  const availableLoadingIds = useLoadingIds()
  const navigate = useNavigate()
  const { user: userInformation } = useUser()
  const { t } = useTranslation()
  const originalInvoiceSignatureData = useRef<boolean>()

  const { permissionAbility } = useAuth()
  const canEditInvoiceSignature = permissionAbility.can(
    'editInvoiceProfile',
    'InvoiceSignature'
  )

  const [invoiceSignatureData, setInvoiceSignatureData] =
    useState<FetchInvoiceSignatureResponse>(emptyInvoiceSignatureInformation)

  // fetchInvoiceSignature
  const { triggerFetch: fetchInvoiceSignature } = useEndpoint({
    loadingId: availableLoadingIds.Invoice.fetchInvoiceSignature,
    async swaggerCall() {
      const fetchedInvoiceSignature = await invoicesApi.fetchInvoiceSignature(
        {}
      )
      setInvoiceSignatureData(fetchedInvoiceSignature)
      // load the reference
      originalInvoiceSignatureData.current =
        fetchedInvoiceSignature.useDefaultSignature
    },
    failureCallback: async (error, done) => {
      const { status } = error as Response
      /**
       * A 404 in this case does not represent a failure but an expected
       * situation where there is simply no saved signature, and a default
       * one is generated based on the user's information.
       */
      done()
      if (status !== 404) {
        setSnackbarMessage(
          t(
            'InvoiceProfileLayout.FetchInvoiceSignature.ValidationMessage.Error',
            'Something went wrong while fetching the Invoice Signature'
          )
        )
        setSnackbarSeverity(SnackbarSeverity.Error)
        setSnackbarState(true)
      }

      originalInvoiceSignatureData.current = true
      userInformation &&
        setInvoiceSignatureData({
          ...mapUserToInvoiceSignature(userInformation),
          useDefaultSignature: true,
        })
    },
  })

  useEffect(() => {
    if (!canEditInvoiceSignature) {
      navigate(
        {
          pathname: '/account/profile',
        },
        {
          replace: true,
        }
      )
    }
    /**
     * Let's wait for the value of userContext to call fetchInvoiceSignature since
     * we need it if no invoiceSignature record is found.
     */
    if (userInformation) fetchInvoiceSignature()
  }, [
    navigate,
    canEditInvoiceSignature,
    fetchInvoiceSignature,
    userInformation,
  ])

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    if (addressField.includes(name)) {
      setInvoiceSignatureData((prev) => {
        return {
          ...prev,
          address: {
            ...defaultAddress,
            ...prev.address,
            [name]: value,
          },
        }
      })
    } else {
      setInvoiceSignatureData({ ...invoiceSignatureData, [name]: value })
    }
  }

  if (invoiceSignatureData.invoiceSenderKey === 0) {
    return <LoadingProgress />
  }

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <InvoiceSignatureForm
        invoiceSignatureDetails={invoiceSignatureData}
        handleInputChange={handleInputChange}
        originUseDefaultSignature={!!originalInvoiceSignatureData.current}
        refresh={fetchInvoiceSignature}
      />
      <InvoiceSignaturePreview
        invoiceSignature={{
          ...(!invoiceSignatureData.useDefaultSignature
            ? invoiceSignatureData
            : mapUserToInvoiceSignature(userInformation as UserProfile)),
        }}
      />
    </Box>
  )
}
